import type { MouseEvent } from 'react';
import { useMemo } from 'react';

import type { ComponentWithChildren } from '@polyai/common/types/helpers';

import { cn } from 'utils/cn';
import { CheckboxCheckIcon } from '../Icons/CheckboxCheckIcon';
import { CheckboxMinusIcon } from '../Icons/CheckboxMinusIcon';

export enum CheckboxState {
  INDETERMINATE,
  UNCHECKED,
  CHECKED,
}

type CheckboxProps = {
  state: CheckboxState;
  onChange?: (e: any) => void;
  isDarkBackground?: boolean;
  disabled?: boolean;
  checkboxWrapperStyles?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

const Checkbox: ComponentWithChildren<CheckboxProps> = ({
  state,
  onChange,
  isDarkBackground = false,
  disabled = false,
  children,
  checkboxWrapperStyles: _checkboxWrapperStyles,
  className,
  ...props
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing
}) => {
  const onClickHandler = (event: MouseEvent<HTMLElement>) => {
    const newChecked = [
      CheckboxState.INDETERMINATE,
      CheckboxState.CHECKED,
    ].includes(state)
      ? false
      : true;

    const checkEventTarget = { ...event.target, checked: newChecked };
    event.target = checkEventTarget;
    onChange && onChange(event);
  };

  const iconColor = useMemo(
    () =>
      disabled
        ? 'iconDisabled'
        : isDarkBackground
          ? 'iconPrimary'
          : 'iconPrimaryInverse',
    [isDarkBackground, disabled],
  );

  const isCheckedOrIndeterminate = [
    CheckboxState.INDETERMINATE,
    CheckboxState.CHECKED,
  ].includes(state);

  const bgClass =
    disabled && isCheckedOrIndeterminate
      ? 'bg-background-disabled-subtle'
      : isDarkBackground && isCheckedOrIndeterminate
        ? 'bg-background-primary-inverse'
        : isCheckedOrIndeterminate
          ? 'bg-background-primary'
          : '';

  const borderClass =
    (isDarkBackground || disabled) && state === CheckboxState.UNCHECKED
      ? 'border-border-strong-inverse'
      : state === CheckboxState.UNCHECKED
        ? 'border-border-strong'
        : 'border-transparent';

  const hoverBgClass =
    isDarkBackground && isCheckedOrIndeterminate
      ? 'hover:bg-background-primary-inverse-hover'
      : isCheckedOrIndeterminate
        ? 'hover:bg-background-inverse'
        : '';

  const hoverFocusBorderClass = isDarkBackground
    ? 'hover:border-border-inverse-focus focus:border-border-inverse-focus'
    : 'hover:border-border-focus focus:border-border-focus';

  const ariaChecked =
    state === CheckboxState.INDETERMINATE
      ? ('mixed' as const)
      : state === CheckboxState.CHECKED;

  const Element = !onChange ? 'div' : 'button';

  return (
    <Element
      aria-checked={onChange ? ariaChecked : undefined}
      role={onChange ? 'checkbox' : undefined}
      type={!onChange ? undefined : 'button'}
      onClick={onClickHandler}
      className={cn(
        'flex items-center justify-center w-[20px] h-[20px] border-xxs2 border-solid rounded-xSmall transition-all duration-200 ease-in-out focus:outline-none',
        bgClass,
        borderClass,
        hoverBgClass,
        hoverFocusBorderClass,
        className,
      )}
      {...props}
    >
      {state === CheckboxState.INDETERMINATE && (
        <CheckboxMinusIcon color={iconColor} size="sm" />
      )}
      {state === CheckboxState.CHECKED && (
        <CheckboxCheckIcon color={iconColor} size="sm" />
      )}
      {children}
    </Element>
  );
};

export default Checkbox;
